import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Flex, Text } from "@radix-ui/themes";
import { motion, useInView } from "motion/react";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import {
  HeadphoneIcon,
  LockIcon,
  CoinIcon,
  BellIcon,
  SoundWaveIcon,
  HeartFilledLikeIcon,
  MoodIcon,
} from "@/assets/icons";
import DarkFeatureCard from "./DarkFeatureCard";
import GhostCard from "./GhostCard";

const slideUp = {
  hidden: { opacity: 0, y: 60 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1] as const,
      delay: i * 0.08,
    },
  }),
};

const staggerTitle = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const },
  },
};

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const },
  },
};

export default function FeatureCardsSection() {
  const cardsRef = useRef<HTMLDivElement>(null);
  const cardsInView = useInView(cardsRef, { once: true, margin: "-80px" });
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: "-60px" });

  return (
    <section className="pt-16 pb-20 md:pt-[100px] md:pb-[160px] lg:pt-[160px] lg:pb-[240px]">
      <ContainerWrapper maxWidth="1400px">
        <div ref={cardsRef}>
          <Flex direction="column" className="mx-auto max-w-[1200px] gap-4">
            {/* Row 1: cols 1-3 = title, col 4 = Unlimited Streaming */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 lg:items-start">
              <motion.div
                className="col-span-2 lg:col-span-3"
                variants={staggerTitle}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <Flex
                  direction="column"
                  className="gap-4 justify-center h-full"
                >
                  <motion.div variants={fadeUp}>
                    <h2 className="font-figtree text-[46px] md:text-[80px] font-black leading-[1.0] md:tracking-[-0.8px] text-blue-900">
                      SUPPORT ARTISTS
                      <br />
                      <span>DIRECTLY </span>
                      <span className="inline-block -translate-y-2.5 -rotate-3 align-middle font-permanent-marker text-[24px] md:text-[32px] font-normal leading-[110%] text-blue-600">
                        Real Fans, Real Impact
                      </span>
                    </h2>
                  </motion.div>
                  <motion.div variants={fadeUp}>
                    <Text className="font-open-sans text-[18px] md:text-[24px] md:inline-block md:max-w-[590px] leading-[1.3] text-[#464C59] tracking-[-0.24px]">
                      Your purchase goes straight to the artist in real time,
                      helping fuel the music you love.
                    </Text>
                  </motion.div>
                </Flex>
              </motion.div>
              <motion.div
                className="col-span-2 lg:col-span-1"
                variants={slideUp}
                custom={0}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <DarkFeatureCard
                  icon={<HeadphoneIcon className="w-6 h-6 lg:w-7 lg:h-7" />}
                  iconColor="text-[#43DDA7]"
                  title="Unlimited Streaming"
                  description="Ownership doesn't mean inconvenience. Stream your library anywhere, anytime. No monthly fees required."
                  className="h-full p-4 md:min-h-[220px] lg:min-h-[280px] lg:p-8"
                />
              </motion.div>
            </div>

            {/* Row 2: col 1 empty, col 2 heart ghost, col 3 True Ownership, col 4 100% Fair */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:grid-cols-4 items-stretch">
              <div className="hidden lg:block" aria-hidden />
              <motion.div
                className="hidden lg:block"
                variants={slideUp}
                custom={1}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <GhostCard>
                  <HeartFilledLikeIcon className="h-[100px] w-[100px]" />
                </GhostCard>
              </motion.div>
              <motion.div
                variants={slideUp}
                custom={2}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <DarkFeatureCard
                  icon={<LockIcon className="w-6 h-6 lg:w-7 lg:h-7" />}
                  iconColor="text-[#40FFB0]"
                  title="True Ownership"
                  description="Build a permanent collection. No 'expiring' subscriptions or disappearing tracks. The music is yours to keep."
                  className="h-full p-4 md:min-h-[220px] lg:min-h-[280px] lg:p-8"
                />
              </motion.div>
              <motion.div
                variants={slideUp}
                custom={3}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <DarkFeatureCard
                  icon={<CoinIcon className="w-6 h-6 lg:w-7 lg:h-7" />}
                  iconColor="text-[#7C9AED]"
                  title="100% Fair Trade Music"
                  description="Artists set their own prices and see the impact immediately. Your support goes exactly where it's intended."
                  className="h-full p-4 md:min-h-[220px] lg:min-h-[280px] lg:p-8"
                />
              </motion.div>
            </div>

            {/* Row 3: col 1 soundwave ghost, col 2 Show Some Extra Love, col 3 Listen to it First, col 4 headphones ghost */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:grid-cols-4 items-stretch">
              <motion.div
                className="hidden lg:block"
                variants={slideUp}
                custom={4}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <GhostCard>
                  <SoundWaveIcon className="h-[100px] w-[100px]" />
                </GhostCard>
              </motion.div>
              <motion.div
                variants={slideUp}
                custom={5}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <DarkFeatureCard
                  icon={
                    <span className="text-[#EF2C4D] flex">
                      <HeartFilledLikeIcon className="w-6 h-6 lg:w-7 lg:h-7" />
                    </span>
                  }
                  iconColor=""
                  title="Show Some Extra Love"
                  description="Love the artist? You can always choose to pay more for the music you believe in."
                  className="h-full p-4 md:min-h-[220px] lg:min-h-[280px] lg:p-8"
                />
              </motion.div>
              <motion.div
                variants={slideUp}
                custom={6}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <DarkFeatureCard
                  icon={<BellIcon className="w-6 h-6 lg:w-7 lg:h-7" />}
                  iconColor="text-[#A17FEA]"
                  title="Listen to it First"
                  description="Skip the wait. Access exclusive new releases on Lutely before they ever hit major streaming platforms."
                  className="h-full p-4 md:min-h-[220px] lg:min-h-[280px] lg:p-8"
                />
              </motion.div>
              <motion.div
                className="hidden lg:block"
                variants={slideUp}
                custom={7}
                initial="hidden"
                animate={cardsInView ? "visible" : "hidden"}
              >
                <GhostCard>
                  <HeadphoneIcon className="h-[100px] w-[100px]" />
                </GhostCard>
              </motion.div>
            </div>

            {/* Find Music Now button — stagger fade in */}
            <div ref={ctaRef}>
              <Flex className="justify-center pt-8 md:pt-[64px]">
                <motion.div
                  variants={fadeIn}
                  initial="hidden"
                  animate={ctaInView ? "visible" : "hidden"}
                >
                  <Link
                    to="/marketplace"
                    className="inline-flex rounded-full bg-blue-100 p-2"
                  >
                    <div className="rounded-full bg-blue-200 p-2">
                      <div className="flex h-16 items-center gap-2 rounded-full bg-blue-600 px-7 text-white font-bold text-[18px] hover:brightness-90 transition-all">
                        <MoodIcon className="size-7" />
                        Find Music Now!
                      </div>
                    </div>
                  </Link>
                </motion.div>
              </Flex>
            </div>
          </Flex>
        </div>
      </ContainerWrapper>
    </section>
  );
}
